import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Editor from '@monaco-editor/react';
import { useTranslation } from '../../../app/LanguageContext';
import {
  compileTemplateSpecForEditor,
  defaultInstanceDocument,
  HIGHLIGHTS_BLOCKDEF_SNIPPET,
  PRODUCT_BLOCKDEF_SNIPPET,
  SYSTEM_PRODUCTS_LOOP_SNIPPET,
  parseTemplateSpec,
  serializeTemplateSpec,
} from '../private/templateSpecUtils';
import TemplateVariablesDrawer from './TemplateVariablesPanel';
import TemplateStudioGuide from './TemplateStudioGuide';
import CatalogPreviewIframe, { PreviewModeToolbar } from './CatalogPreviewIframe';
import { captureCatalogTemplateDesktopPreview } from '../private/catalogTemplatePreviewCapture';

const PREVIEW_DEBOUNCE_MS = 1500;
const STUDIO_VIEWPORT_OFFSET_PX = 220;

const themeFields = [
  { group: 'colors', key: 'primary', labelKey: 'catalog.templates.theme_primary' },
  { group: 'colors', key: 'accent', labelKey: 'catalog.templates.theme_accent' },
  { group: 'colors', key: 'background', labelKey: 'catalog.templates.theme_background' },
  { group: 'colors', key: 'text', labelKey: 'catalog.templates.theme_text' },
  { group: 'colors', key: 'muted', labelKey: 'catalog.templates.theme_muted' },
];

/* Monaco snippets use ${n:placeholder} — not JS template literals. */
/* eslint-disable no-template-curly-in-string */
const registerHtmlSnippets = (monaco) => {
  monaco.languages.registerCompletionItemProvider('html', {
    triggerCharacters: ['{'],
    provideCompletionItems: () => ({
      suggestions: [
        {
          label: 'blockdef',
          kind: monaco.languages.CompletionItemKind.Snippet,
          documentation: 'Define um bloco editável',
          insertText: [
            '{{ blockdef "${1:block_id}" label="${2:Label}" }}',
            '{{ field "${3:field_key}" type="text" label="${4:Label}" }}',
            '$0',
            '{{ endblockdef }}',
          ].join('\n'),
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'field text',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ field "${1:key}" type="text" label="${2:Label}" default=mock.text(size: "80") }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'mock.image',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ mock.image${1:(size: "400x500")} }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'mock.text',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ mock.text${1:(size: "120")} }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'mock.email',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ mock.email }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'mock.url',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ mock.url }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'field default mock.image',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText:
            '{{ field "${1:key}" type="media" label="${2:Imagem}" default=mock.image(size: "400x500", extension: "png") }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'field item_list',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText:
            '{{ field "${1:items}" type="item_list" label="${2:Itens}" demo_count="${3:3}" min_items="${4:1}" max_items="${5:12}" }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'block highlights',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: HIGHLIGHTS_BLOCKDEF_SNIPPET,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'block products',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: PRODUCT_BLOCKDEF_SNIPPET,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'system.products loop',
          kind: monaco.languages.CompletionItemKind.Snippet,
          documentation: 'Loop com demo_count explícito (preview/catálogo vazio)',
          insertText: SYSTEM_PRODUCTS_LOOP_SNIPPET,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'for demo_count',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ for ${1:product} in ${2:block.products} demo_count="${3:6}" }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'variable default mock',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ ${1:product.title} default=mock.${2:title}() }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'if chance else',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText:
            '{{ if ${1:sku.has_price} chance="${2:50%}" }}\n  ${3:<!-- true -->}\n{{ else }}\n  ${4:<!-- false -->}\n{{ end }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'field product_list',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText:
            '{{ field "${1:products}" type="product_list" label="${2:Produtos}" demo_count="${3:3}" min_items="${4:0}" max_items="${5:24}" }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
        {
          label: 'block ref',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{ block "${1:block_id}" }}',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        },
      ],
    }),
  });
};

const withCompiledBlocks = (spec) => {
  try {
    return compileTemplateSpecForEditor(spec);
  } catch {
    return spec;
  }
};

const TemplateStudioEditor = forwardRef(({ documentJson, onDocumentChange, catalogId, refreshKey = 0, fileMode = false }, ref) => {
  const { translate } = useTranslation();
  const [tab, setTab] = useState('html');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [variablesOpen, setVariablesOpen] = useState(false);
  const snippetsRegisteredRef = useRef(false);

  const specRef = useRef(parseTemplateSpec(documentJson));
  const loadedDocumentRef = useRef(documentJson);
  const [editorSpec, setEditorSpec] = useState(() => parseTemplateSpec(documentJson));
  const htmlEditorRef = useRef(null);
  const cssEditorRef = useRef(null);

  const [stableDocJson, setStableDocJson] = useState(documentJson);
  const previewTimerRef = useRef(null);

  useEffect(() => {
    if (documentJson === loadedDocumentRef.current) return;
    loadedDocumentRef.current = documentJson;
    const parsed = parseTemplateSpec(documentJson);
    specRef.current = parsed;
    setEditorSpec(parsed);
  }, [documentJson]);

  useEffect(() => {
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      setStableDocJson(serializeTemplateSpec(specRef.current));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(previewTimerRef.current);
  }, [editorSpec]);

  useEffect(() => {
    setStableDocJson(documentJson);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const flushActiveEditor = () => {
    if (tab === 'html' && htmlEditorRef.current) {
      updateSpec({ html: htmlEditorRef.current.getValue() || '' });
    } else if (tab === 'css' && cssEditorRef.current) {
      updateSpec({ css: cssEditorRef.current.getValue() || '' });
    }
  };

  useImperativeHandle(ref, () => ({
    getDocumentJson: () => {
      flushActiveEditor();
      return serializeTemplateSpec(specRef.current);
    },
    captureDesktopPreview: async () => {
      flushActiveEditor();
      const spec = withCompiledBlocks(specRef.current);
      const templateJson = serializeTemplateSpec(spec);
      const instanceJson = JSON.stringify(
        defaultInstanceDocument(spec, { usePlaceholders: true }),
      );
      return captureCatalogTemplateDesktopPreview({
        catalogId,
        templateJson,
        instanceJson,
      });
    },
  }));

  const emitSpec = (nextSpec) => {
    specRef.current = nextSpec;
    setEditorSpec(nextSpec);
    const serialized = serializeTemplateSpec(nextSpec);
    loadedDocumentRef.current = serialized;
    onDocumentChange?.(serialized);
  };

  const updateSpec = (patch) => {
    const merged = { ...specRef.current, ...patch };
    emitSpec(withCompiledBlocks(merged));
  };

  const handleTabChange = (_, value) => {
    if (tab === 'html' && htmlEditorRef.current) {
      updateSpec({ html: htmlEditorRef.current.getValue() || '' });
    }
    if (tab === 'css' && cssEditorRef.current) {
      updateSpec({ css: cssEditorRef.current.getValue() || '' });
    }
    setTab(value);
  };

  const updateThemeToken = (group, key, value) => {
    emitSpec({
      ...specRef.current,
      theme: {
        ...specRef.current.theme,
        [group]: { ...(specRef.current.theme?.[group] || {}), [key]: value },
      },
    });
  };

  const demoInstanceJson = useMemo(() => {
    const spec = withCompiledBlocks(editorSpec);
    return JSON.stringify(defaultInstanceDocument(spec, { usePlaceholders: true }));
  }, [editorSpec]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: 0,
        height: `calc(100vh - ${STUDIO_VIEWPORT_OFFSET_PX}px)`,
        overflow: 'hidden',
      }}
    >
      <TemplateStudioGuide onOpenVariables={() => setVariablesOpen(true)} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: 2,
          minHeight: 0,
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Tab value="html" label={translate('catalog.templates.tab_html')} />
              <Tab value="css" label={translate('catalog.templates.tab_css')} />
              {!fileMode && (
                <Tab value="theme" label={translate('catalog.templates.tab_theme')} />
              )}
            </Tabs>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, mr: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                {translate('catalog.templates.variables_search_placeholder')}
              </Typography>
              <Button size="small" startIcon={<InfoOutlinedIcon />} onClick={() => setVariablesOpen(true)}>
                {translate('catalog.templates.variables_open')}
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                height: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                p: tab === 'theme' ? 2 : 0,
              }}
            >
              {tab === 'html' && (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Editor
                    height="100%"
                    language="html"
                    theme="vs-light"
                    value={editorSpec.html || ''}
                    onChange={(value) => updateSpec({ html: value || '' })}
                    onMount={(editor, monaco) => {
                      htmlEditorRef.current = editor;
                      if (!snippetsRegisteredRef.current) {
                        registerHtmlSnippets(monaco);
                        snippetsRegisteredRef.current = true;
                      }
                    }}
                    options={{ minimap: { enabled: false }, wordWrap: 'on', fontSize: 13 }}
                  />
                </Box>
              )}
              {tab === 'css' && (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <Editor
                    height="100%"
                    language="css"
                    theme="vs-light"
                    value={editorSpec.css || ''}
                    onChange={(value) => updateSpec({ css: value || '' })}
                    onMount={(editor) => {
                      cssEditorRef.current = editor;
                    }}
                    options={{ minimap: { enabled: false }, wordWrap: 'on', fontSize: 13 }}
                  />
                </Box>
              )}
              {tab === 'theme' && (
                <Box sx={{ display: 'grid', gap: 1.5, overflow: 'auto', flex: 1, minHeight: 0 }}>
                  {themeFields.map((field) => (
                    <TextField
                      key={field.key}
                      label={translate(field.labelKey)}
                      size="small"
                      value={editorSpec.theme?.[field.group]?.[field.key] || ''}
                      onChange={(e) => updateThemeToken(field.group, field.key, e.target.value)}
                    />
                  ))}
                </Box>
              )}
            </Box>
            <TemplateVariablesDrawer
              open={variablesOpen}
              onClose={() => setVariablesOpen(false)}
            />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          <Box
            sx={{
              p: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2">{translate('catalog.templates.preview_title')}</Typography>
            <PreviewModeToolbar mode={previewMode} onChange={setPreviewMode} />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <CatalogPreviewIframe
              catalogId={catalogId}
              templateJson={stableDocJson}
              instanceJson={demoInstanceJson}
              previewMode={previewMode}
              refreshKey={`${stableDocJson?.length}-${previewMode}`}
              allowSynthetic
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
});

TemplateStudioEditor.displayName = 'TemplateStudioEditor';

export default TemplateStudioEditor;
